import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Settings as SettingsIcon } from 'lucide-react';
import { formatPeriod, parsePeriod, shiftPeriod } from '@trestly/shared';
import { usePeriod } from '../period-context';
import { monthName } from '../lib/format';
import { haptic, insideTelegram } from '@/telegram';
import { cn } from '@/lib/utils';
import { t } from '../i18n';

interface ScreenHeaderProps {
  title: ReactNode;
  /** Строка под заголовком: «перерасход в 1 конверте». */
  meta?: ReactNode;
  /** Вложенный экран: слева «назад» вместо периода. В клиенте — системная кнопка. */
  onBack?: () => void;
  /** Показывать ли переключатель периода. На настройках он не нужен. */
  period?: boolean;
  /** Справа от заголовка: «+», «Изменить». */
  action?: ReactNode;
}

/**
 * Шапка экрана: ряд с периодом и крупный заголовок (Large Title по HIG).
 *
 * В полноэкранном режиме Telegram рисует поверх приложения свои кнопки
 * («Закрыть» слева, «⌄ ⋯» справа). Ряд с периодом встаёт ровно в эту полосу,
 * по центру между ними: место не пропадает, и ничего не перекрыто. Настройки —
 * круглая кнопка у заголовка (и пункт в меню «⋯» клиента).
 */
export function ScreenHeader({
  title,
  meta,
  onBack,
  period = true,
  action,
}: ScreenHeaderProps): JSX.Element {
  const navigate = useNavigate();
  // В клиенте «назад» — системная кнопка (useBackButton), своя стрелка — только в браузере.
  const showOwnBack = onBack !== undefined && !insideTelegram();
  const showPeriod = period;

  return (
    <header className="screen-header px-5 pb-4">
      <div className="screen-bar">
        {showOwnBack ? (
          <button
            type="button"
            className="screen-bar-start -ml-2 flex h-9 items-center gap-0.5 rounded-full pr-3 pl-1 text-[16px] text-accent active:opacity-60"
            onClick={() => {
              haptic.tap('soft');
              onBack();
            }}
          >
            <ChevronLeft className="size-[22px]" aria-hidden />
            {t('components.back')}
          </button>
        ) : null}
        {showPeriod ? <PeriodPill /> : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-[32px] leading-tight font-bold tracking-[-0.022em]">{title}</h1>
          {meta ? <div className="mt-0.5 text-[14px] text-muted-foreground">{meta}</div> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {action}
          {onBack === undefined ? (
            // Настройки есть и в меню клиента, но вход в шапке нужен всегда: меню находят не все.
            <button
              type="button"
              aria-label={t('components.settings')}
              className="icon-btn text-muted-foreground"
              onClick={() => {
                haptic.tap('soft');
                navigate('/settings');
              }}
            >
              <SettingsIcon className="size-[19px]" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

/**
 * Период — компактная таблетка «‹ Октябрь ›». Не текущий период — приглушён,
 * тап по названию возвращает к текущему.
 */
export function PeriodPill(): JSX.Element {
  const { period, setPeriod, todayPeriod } = usePeriod();
  const key = parsePeriod(period);
  const isCurrent = period === todayPeriod;
  const shift = (delta: -1 | 1): void => {
    haptic.select();
    const next = formatPeriod(shiftPeriod(key, delta));
    setPeriod(next === todayPeriod ? null : next);
  };
  const today = parsePeriod(todayPeriod);
  const label = key.year === today.year ? monthName(key.month) : `${monthName(key.month)} ${key.year}`;
  const arrow = 'flex size-8 items-center justify-center rounded-full text-muted-foreground active:opacity-50';

  return (
    <div className="period-pill flex h-[34px] items-center rounded-full px-0.5">
      <button type="button" className={arrow} aria-label={t('components.prevPeriod')} onClick={() => shift(-1)}>
        <ChevronLeft className="size-4" strokeWidth={2.4} aria-hidden />
      </button>
      <button
        type="button"
        className={cn(
          'min-w-[4.5rem] px-0.5 text-center text-[14.5px] font-semibold whitespace-nowrap capitalize',
          !isCurrent && 'text-accent',
        )}
        aria-live="polite"
        aria-label={isCurrent ? label : t('components.backToCurrent', { label })}
        disabled={isCurrent}
        onClick={() => {
          haptic.select();
          setPeriod(null);
        }}
      >
        {label}
      </button>
      <button type="button" className={arrow} aria-label={t('components.nextPeriod')} onClick={() => shift(1)}>
        <ChevronRight className="size-4" strokeWidth={2.4} aria-hidden />
      </button>
    </div>
  );
}

/** Круглая кнопка действия в шапке: «+». */
export function HeaderAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      className="icon-btn"
      onClick={() => {
        haptic.tap();
        onClick();
      }}
    >
      {children}
    </button>
  );
}